import { useEffect, useRef } from 'react';
import { isReadingForName, lookupNames } from '../../services/documentNamingService';

// Every 3 s for the first minute (the reader usually answers in seconds), then
// every 15 s up to ten minutes, the same ceiling the server gives a reading
// (SUGGESTION_WAIT_MINUTES) before its lists say no suggestion came.
const QUICK_MS = 3000;
const QUICK_TICKS = 20;
const SLOW_MS = 15000;
const SLOW_TICKS = 36;

const readingIdsOf = (namesById) => Object.entries(namesById || {})
  .filter(([, row]) => isReadingForName(row))
  .map(([id]) => id);

/**
 * While any document in ``namesById`` is still being read for a name, ask the
 * server for those names again and merge the answer in (ClickUp 1248h53bmhy:
 * the suggestion arrives next to the document a few seconds after the
 * upload). The clock restarts whenever the set of documents being read
 * changes (a new upload), stops by itself once nothing is reading or the
 * ceiling is reached, and when the list goes away. No always-on connection.
 */
export default function useSuggestionPolling(namesById, setNamesById, moduleFilter = '') {
  const namesRef = useRef(namesById);
  namesRef.current = namesById;
  // The effect is keyed on WHICH documents are reading, not on the map object:
  // every merge makes a new map, and that must not restart the clock.
  const readingKey = readingIdsOf(namesById).sort().join(',');

  useEffect(() => {
    if (!readingKey) return undefined;
    let alive = true;
    let ticks = 0;
    let timer = null;
    const tick = async () => {
      if (!alive) return;
      ticks += 1;
      const ids = readingIdsOf(namesRef.current);
      if (ids.length) {
        try {
          const fresh = await lookupNames(ids, moduleFilter);
          if (alive && fresh && Object.keys(fresh).length) {
            setNamesById((prev) => ({ ...(prev || {}), ...fresh }));
          }
        } catch (err) {
          // The next tick asks again; a lookup that fails changes nothing on screen.
          console.warn('Name suggestion check failed (will retry):', err);
        }
      }
      if (!alive || ticks >= QUICK_TICKS + SLOW_TICKS) return;
      timer = setTimeout(tick, ticks < QUICK_TICKS ? QUICK_MS : SLOW_MS);
    };
    timer = setTimeout(tick, QUICK_MS);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [readingKey, setNamesById, moduleFilter]);
}
